import { action } from '@reatom/framework'
import { match } from 'ts-pattern'

import { identity, pipe, Result } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { premiumMetaUpdateReceived } from '@root/entities/premium/atoms'
import { TxSerialized, type TxSigned } from '@root/entities/transaction'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { Error } from '@root/shared'
import { ApiError, apiRequest, type PremiumApi } from '@root/shared/api'
import { PremiumErrorCode } from '@root/shared/api/api-error-code'

import type { OmitKey, TypeHelper } from '@blade/std-lib'
import type {
  PremiumExtendCreatePayload,
  PremiumGetExtendIxsCreatePayload,
} from '@blade/trade-api-client'
import type { ActionPayload } from '@reatom/framework'
import type { WalletId } from '@root/entities/wallet'

export interface ExtendPremiumRequest
  extends OmitKey<PremiumApi.ExtendPremiumReqDtp, 'signedTx' | 'walletId'> {
  signedTx: TxSigned
  walletId: WalletId
}

export const fetchExtendPremiumInstructionsRequestAction = action(
  async (ctx, request: PremiumGetExtendIxsCreatePayload) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.premium.premiumGetExtendIxsCreate, [
        request,
      ]),
    )
    const mappedResult = pipe(
      result,
      Result.map((value) => ({
        ...value,
        serializedTransaction: TxSerialized(value.serializedTransaction),
      })),
      Result.mapError((e) => ApiError.fallbackToUnknown(e)),
    )

    return mappedResult
  },
  'fetchExtendPremiumInstructionsRequestAction',
)

export const extendPremiumRequestAction = action(
  async (ctx, request: ExtendPremiumRequest) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.premium.premiumExtendCreate, [request]),
    )
    const mappedResult = pipe(
      result,
      Result.mapError((e) =>
        match(e)
          .when(
            ApiError.isOfCodes([
              PremiumErrorCode.PremiumNotFound,
              PremiumErrorCode.ExpireTimeMoreThan7Days,
              PremiumErrorCode.PremiumAlreadyPurchased,
              PremiumErrorCode.TransactionNotFound,
            ]),
            identity,
          )
          .otherwise(() => e),
      ),
      Result.map((it) => ({
        currency: it,
        premium: it.premium,
      })),
    )

    if (Result.isSuccess(mappedResult)) {
      premiumMetaUpdateReceived(ctx, {
        currency: mappedResult.success.currency,
        premiumMeta: mappedResult.success.premium,
      })
    }

    return mappedResult
  },
  'extendPremiumRequestAction',
)

type ExtendResult = Awaited<ActionPayload<typeof extendPremiumRequestAction>>

export type ExtendPremiumError = Result.Result.Failure<ExtendResult>

interface ExtendPremiumActionRequest
  extends TypeHelper.OmitKey<
    PremiumExtendCreatePayload,
    'signedTx' | 'walletId'
  > {
  walletId: WalletId
}

export const extendPremiumAction = action(
  async (ctx, request: ExtendPremiumActionRequest) => {
    const { secureStorageService } = ctx.get(dependenciesAtom)

    const instructionsResult = await ctx.schedule(() =>
      fetchExtendPremiumInstructionsRequestAction(ctx, request),
    )

    if (Result.isFailure(instructionsResult)) {
      return Result.fail(instructionsResult.failure)
    }

    const signedTxResult = await ctx.schedule(() =>
      secureStorageService.signTransaction({
        tx: instructionsResult.success.serializedTransaction,
        operation: 'booster',
        network: Network.fromDto(request.network),
        walletId: request.walletId,
        password: undefined,
        // TODO: accept valid amount used in transaction.
        amount: '0',
      }),
    )

    if (Result.isFailure(signedTxResult)) {
      return Result.fail(Error.UnknownError.fromUnknown(signedTxResult))
    }

    const result = await ctx.schedule(() =>
      extendPremiumRequestAction(ctx, {
        ...request,
        signedTx: signedTxResult.success.txSigned,
      }),
    )

    return result
  },
  'extendPremiumAction',
)
